/**
 * Habillage commun des modales à formulaire (AddEleve, Addgroupe) :
 * overlay + carte (slideIn) + en-tête dégradé avec triangle + footer Annuler/Action.
 * Ne fige QUE le layout externe — le corps (champs) est fourni via `children`, et
 * les classes responsives propres à chaque modale passent par les props `*ClassName`
 * (defaults = style « AddEleve », non responsive).
 *
 * Props : show, onClose, onSubmit, isSubmitting, modalRef, onKeyDown,
 *   title, subtitle, actionLabel, submittingLabel, actionStyle, actionDisabled,
 *   cardClassName, headerClassName, bodyClassName, titleClassName, subtitleClassName,
 *   buttonClassName, beforeBody, children
 *
 * `actionDisabled` (faux par défaut) grise le bouton d'action tant que le
 * formulaire ne peut pas être envoyé (Addgroupe : moins de deux classes).
 */
import { createPortal } from 'react-dom';

const FormModalShell = ({
  show,
  onClose,
  onSubmit,
  isSubmitting,
  modalRef,
  onKeyDown,
  title,
  subtitle,
  actionLabel,
  submittingLabel,
  actionStyle,
  actionDisabled = false,
  cardClassName = 'max-w-md',
  headerClassName = 'p-8',
  bodyClassName = 'p-8 space-y-6',
  titleClassName = 'text-2xl font-semibold mb-2',
  subtitleClassName = 'text-sm',
  buttonClassName = 'px-6 py-3 xl:text-lg text-sm',
  beforeBody = null,
  children,
}) => {
  if (!show) return null;

  // Portal vers <body> : `fixed inset-0` est ainsi relatif au VIEWPORT et non à
  // un ancêtre transformé/filtré (backdrop-filter), donc la modale reste centrée
  // à l'écran quel que soit le scroll/overflow de la page.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={`bg-white rounded-3xl shadow-2xl flex flex-col w-full ${cardClassName} overflow-hidden animate-in`}
        style={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #f5f7ff 100%)', animation: 'slideIn 0.3s ease-out' }}
      >
        {/* Header dégradé + triangle */}
        <div
          className={`relative w-full text-center text-white ${headerClassName}`}
          style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}
        >
          <div
            className="absolute left-1/2 w-0 h-0"
            style={{
              bottom: '-10px',
              transform: 'translateX(-50%)',
              borderLeft: '15px solid transparent',
              borderRight: '15px solid transparent',
              borderTop: '10px solid #00f2fe',
            }}
          />
          <h2 className={titleClassName}>{title}</h2>
          <p className={subtitleClassName} style={{ opacity: 0.9 }}>{subtitle}</p>
        </div>

        {/* Slot optionnel entre l'en-tête et le corps (ex. bandeau GAR) */}
        {beforeBody}

        {/* Corps + footer — texte sombre par défaut (carte sur fond clair ; le
            portal change le contexte d'héritage de couleur, on le fige donc ici) */}
        <div className={`text-gray-900 ${bodyClassName}`}>
          {children}

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className={`flex-1 ${buttonClassName} bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold transition-all duration-300 hover:from-orange-600 hover:to-orange-700 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/50`}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting || actionDisabled}
              className={`flex-1 ${buttonClassName} rounded-xl text-white font-semibold transition-all duration-300 hover:-translate-y-0.5 tracking-wide border-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
              style={actionStyle}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  {submittingLabel}
                </>
              ) : (
                actionLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FormModalShell;
